import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import {
  Localidad,
  LocalidadesService,
} from '../../core/services/localidades.service';

/** Espera tras la última tecla antes de consultar sugerencias, en ms. */
const DEBOUNCE_MS = 300;

/**
 * Input de localidad con autocompletado (`GET /localidades?q=`) para el filtro
 * de Ubicación del buscador (FP-16).
 *
 * Portado de `LocalidadAutocompleteComponent` del backoffice. Se conserva lo
 * que hace difícil este componente —el pipeline de consulta, la navegación con
 * teclado y el `mousedown` que evita que el blur mate al click— y se deja
 * afuera toda la maquinaria de formulario (`ControlValueAccessor`, `NgControl`,
 * estados dirty/valid): acá no es un campo de formulario sino un buscador que
 * emite zonas para agregar como chips (DP02/DP03).
 *
 * Se permite texto libre a propósito: el catálogo es abierto y el backend
 * filtra por coincidencia parcial insensible a tildes, así que una zona
 * tipeada a mano igual funciona.
 */
@Component({
  selector: 'app-autocomplete-localidad',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './autocomplete-localidad.html',
  styleUrl: './autocomplete-localidad.scss',
})
export class AutocompleteLocalidad {
  private readonly localidades = inject(LocalidadesService);

  readonly placeholder = input('Ej.: Palermo');

  /** Zona elegida (de una sugerencia o tipeada a mano). */
  readonly elegida = output<string>();

  readonly texto = signal('');
  readonly opciones = signal<Localidad[]>([]);
  readonly abierto = signal(false);
  /** Consulta en vuelo: el desplegable muestra "Buscando…". */
  readonly buscando = signal(false);
  /** Índice resaltado para la navegación con teclado (-1 = ninguno). */
  readonly destacada = signal(-1);

  private readonly listaRef = viewChild<ElementRef<HTMLUListElement>>('lista');
  private readonly consulta$ = new Subject<string>();
  private enfocado = false;

  constructor() {
    // `switchMap` descarta la consulta anterior si el usuario siguió tipeando,
    // y la consulta vacía (campo borrado) cancela una respuesta en vuelo:
    // `null` marca "sin consulta", distinto de "cero resultados".
    // Sin `distinctUntilChanged` a propósito: tras elegir y volver a escribir
    // el mismo texto, el desplegable tiene que reabrirse.
    this.consulta$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap((q) => {
          const limpio = q.trim();
          return limpio
            ? this.localidades.buscar(limpio)
            : of<Localidad[] | null>(null);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((sugerencias) => {
        this.buscando.set(false);
        if (sugerencias === null) {
          this.opciones.set([]);
          this.cerrar();
          return;
        }
        this.opciones.set(sugerencias);
        this.destacada.set(-1);
        // Sólo se abre si el campo sigue en foco: la respuesta puede llegar
        // después de que el usuario salió. Con cero resultados queda abierto
        // mostrando "Sin resultados".
        this.abierto.set(this.enfocado);
      });
  }

  onInput(evento: Event): void {
    const valor = (evento.target as HTMLInputElement | null)?.value ?? '';
    this.texto.set(valor);
    if (valor.trim()) {
      // "Buscando…" inmediato (el debounce demora la consulta, no el estado) y
      // se descartan las sugerencias viejas para no elegir una stale.
      this.buscando.set(true);
      this.opciones.set([]);
      this.destacada.set(-1);
      this.abierto.set(true);
    } else {
      this.buscando.set(false);
      this.cerrar();
    }
    // La consulta viaja SIEMPRE, vacía incluida: así `switchMap` cancela la
    // respuesta en vuelo cuando el usuario borra, en vez de dejarla llegar y
    // reabrir el desplegable sobre un campo vacío.
    this.consulta$.next(valor);
  }

  onFocus(): void {
    this.enfocado = true;
    if (this.texto().trim() && this.opciones().length) this.abierto.set(true);
  }

  onBlur(): void {
    this.enfocado = false;
    this.cerrar();
  }

  onKeydown(evento: KeyboardEvent): void {
    switch (evento.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.abierto()) {
          if (this.opciones().length && this.texto().trim()) {
            this.abierto.set(true);
          }
          break;
        }
        evento.preventDefault();
        const total = this.opciones().length;
        // `abierto` implica opciones, pero un `% 0` dejaría NaN: guard barato.
        if (!total) break;
        const actual = this.destacada();
        // Sin resaltado (-1): ArrowDown arranca en la primera y ArrowUp en la
        // ÚLTIMA. Desde la primera, ArrowUp da la vuelta a la última.
        const siguiente =
          evento.key === 'ArrowDown'
            ? (actual + 1) % total
            : actual <= 0
              ? total - 1
              : actual - 1;
        this.destacada.set(siguiente);
        this.scrollDestacada();
        break;
      }
      case 'Enter': {
        evento.preventDefault();
        const resaltada = this.opciones()[this.destacada()];
        if (this.abierto() && resaltada) {
          this.seleccionar(resaltada);
        } else if (this.texto().trim()) {
          // Texto libre: el catálogo es abierto y el backend filtra por
          // coincidencia parcial.
          this.emitir(this.texto().trim());
        }
        break;
      }
      case 'Escape': {
        if (this.abierto()) {
          evento.stopPropagation();
          this.cerrar();
        }
        break;
      }
    }
  }

  /**
   * El `mousedown` de las opciones hace `preventDefault` en la plantilla para
   * que el input no pierda el foco antes de que llegue este click.
   */
  seleccionar(opcion: Localidad): void {
    this.emitir(opcion.nombre);
  }

  private emitir(zona: string): void {
    this.elegida.emit(zona);
    this.texto.set('');
    this.opciones.set([]);
    this.cerrar();
  }

  private cerrar(): void {
    this.abierto.set(false);
    this.destacada.set(-1);
  }

  private scrollDestacada(): void {
    const item = this.listaRef()?.nativeElement.children[this.destacada()] as
      | HTMLElement
      | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }
}
